// DesignManager.js
import debug from "debug";
import { DataProvider } from "../../Experience/Utils/DataVault/DataProvider.js";
const log = debug("app:xmd");

/**
 * Class to manage design-related operations such as saving,
 * loading, and handling events in the application.
 * @category Components
 * @subcategory DesignManager
 */
export class DesignManager {
  /**
   * @param {Object} experience - The main application experience object.
   */
  constructor(experience) {
    this.experience = experience;
    this.eventHub = experience.eventHub;
    this.bomData = experience.bomData;
    this.user_authenticated = false; // Start as false, will be set to true based on experience.userState
    this.dataProvider = new DataProvider(experience);
    this.isSaving = false; // Guard to prevent concurrent saves
    this.pendingSaveAfterRename = false; // Set when user chose "Rename" in conflict dialog (save context) so we re-save after they submit new name
    this.setupEventListeners();
    this.setupMessageListener();
    this.setupAuthEventListeners();
    // Initialize from centralized userState on experience (if available)
    this.syncAuthStateFromExperience();
  }

  /**
   * Sets up event listeners for design management operations.
   */
  setupEventListeners() {
    this.eventHub.on("saveProject", this.saveDesign.bind(this));
    this.eventHub.on("saveAsBtn", this.saveAsDesign.bind(this));
    this.eventHub.on("saveAsProject", this.saveAsBtn.bind(this));
    this.eventHub.on("sendToCart", this.handleSendToCart.bind(this)); // Listener for sendToCart
    this.eventHub.on("viewProject", this.loadDesignFromApi.bind(this)); // Listener for loading designs from API
    this.eventHub.on(
      "requestNewProject",
      this.handleRequestNewProject.bind(this),
    ); // Listener for new project creation with validation
    this.eventHub.on(
      "confirmOverwriteDesign",
      this.handleConfirmOverwrite.bind(this),
    ); // Listener for overwrite confirmation
    this.eventHub.on(
      "confirmRenameDesign",
      this.handleConfirmRename.bind(this),
    ); // Listener for rename confirmation
    this.eventHub.on("designRenamed", this.handleDesignRenamed.bind(this)); // After user submits new name in rename modal, re-trigger save if needed
  }

  /**
   * Sets up event listeners for authentication state changes.
   * This ensures user_authenticated flag is properly updated on login/logout.
   */
  setupAuthEventListeners() {
    // Listen for centralized auth events and keep local flag in sync
    this.eventHub.on("auth:login", () => {
      log("DesignManager: User logged in");
      this.syncAuthStateFromExperience();
    });

    this.eventHub.on("sign_out", () => {
      log("DesignManager: User signed out");
      this.syncAuthStateFromExperience();
    });

    this.eventHub.on("auth:guest", () => {
      log("DesignManager: User is guest (not authenticated)");
      this.syncAuthStateFromExperience();
    });

    this.eventHub.on("sessionExpired", () => {
      log("DesignManager: Session expired, syncing auth state from experience");
      this.syncAuthStateFromExperience();
    });
  }

  /**
   * Syncs DesignManager.user_authenticated from the centralized experience.userState.
   * Falls back to LoginManager.isAuthenticated() when userState is not available.
   */
  syncAuthStateFromExperience() {
    try {
      const state = this.experience?.userState;
      if (state === "authenticated") {
        this.user_authenticated = true;
        log(
          "DesignManager: user_authenticated set to true from experience.userState",
        );
        return;
      }
      if (state === "guest" || state === "expired") {
        this.user_authenticated = false;
        log(
          "DesignManager: user_authenticated set to false from experience.userState:",
          state,
        );
        return;
      }

      // Fallback: use LoginManager.isAuthenticated if userState is not defined
      const loginManager = this.experience?.LoginManager;
      if (loginManager && typeof loginManager.isAuthenticated === "function") {
        this.user_authenticated = !!loginManager.isAuthenticated();
        log(
          "DesignManager: user_authenticated set from LoginManager.isAuthenticated():",
          this.user_authenticated,
        );
      } else {
        this.user_authenticated = false;
        log(
          "DesignManager: defaulting user_authenticated to false (no userState/LoginManager)",
        );
      }
    } catch (error) {
      log("DesignManager: Error syncing auth state from experience:", error);
      this.user_authenticated = false;
    }
  }

  cleanup() {
    log("DesignManager cleanup started...");

    // 1. Remove event listeners from eventHub
    if (this.eventHub) {
      this.eventHub.off("saveProject", this.saveDesign);
      this.eventHub.off("saveAsBtn", this.saveAsDesign);
      this.eventHub.off("saveAsProject", this.saveAsBtn);
      this.eventHub.off("sendToCart", this.handleSendToCart);
      this.eventHub.off("viewProject", this.loadDesignFromApi);
      this.eventHub.off("requestNewProject", this.handleRequestNewProject);
      this.eventHub.off("confirmOverwriteDesign", this.handleConfirmOverwrite);
      this.eventHub.off("confirmRenameDesign", this.handleConfirmRename);
      this.eventHub.off("designRenamed", this.handleDesignRenamed);
      // Remove auth event listeners
      this.eventHub.off("auth:login");
      this.eventHub.off("sign_out");
      this.eventHub.off("auth:guest");
      this.eventHub.off("sessionExpired");
    }

    // 2. Remove eventHub loadProject listener
    if (this.eventHub) {
      this.eventHub.off("loadProject");
    }

    // 3. Clear timeouts
    if (this.zoomTimeout) {
      clearTimeout(this.zoomTimeout);
      this.zoomTimeout = null;
    }

    // 4. Cleanup DataProvider
    if (this.dataProvider) {
      this.dataProvider.cleanup();
      this.dataProvider = null;
    }

    // 5. Nullify object references
    this.eventHub = null;
    this.bomData = null;
    this.experience = null;

    log("DesignManager cleanup completed.");
  }

  /**
   * Sets up event listeners for loadProject events via eventHub.
   * Replaces postMessage communication with eventHub events.
   */
  setupMessageListener() {
    // Listen for loadProject events via eventHub
    this.eventHub.on("loadProject", (eventData) => {
      log("Received loadProject event via eventHub:", eventData);

      const projectName = eventData?.name;
      const designData = eventData?.contents
        ? typeof eventData.contents === "string"
          ? JSON.parse(eventData.contents)
          : eventData.contents
        : null;

      log("Load project event received. Project name:", projectName);
      log("Design data received:", designData);

      if (projectName === undefined) {
        log("Project name is undefined, showing initial view modals.");

        document.getElementById("backgroundModal").style.display = "flex";
        document.getElementById("loadModal").style.display = "flex";

        if (this.user_authenticated) {
          log("User is authenticated, showing user initial view.");

          document.getElementById("user-initial-view").style.display = "flex";
          document.getElementById("guest-initial-view").style.display = "none";
        } else {
          log("User is not authenticated, showing guest initial view.");

          document.getElementById("guest-initial-view").style.display = "flex";
          document.getElementById("user-initial-view").style.display = "none";
        }
      } else {
        log("Project name defined, hiding modals and loading project.");

        document.getElementById("backgroundModal").style.display = "none";
        document.getElementById("loadModal").style.display = "none";

        const projectNameElement = document.getElementById(
          "toolbar-project-name",
        );
        const projectNameContainer = document.getElementById(
          "toolbar-project-name-container",
        );
        if (projectNameElement) {
          projectNameElement.value = projectName;
        }
        if (projectNameContainer) {
          projectNameContainer.style.display = "flex";
        }

        if (designData) {
          log("Loading design data:", eventData);
          this.eventHub.emit("deleteScene");
          this.eventHub.emit("MakePartsEvent", {
            eventType: "load",
            data: designData,
          });

          setTimeout(() => {
            this.eventHub.emit("zoomAll");
          }, 2500);
        }
      }
    });
  }

  /**
   * Checks if a design name already exists and returns the matching design if found.
   * @param {string} designName - The design name to check.
   * @returns {Promise<Object|null>} Promise that resolves to the design object if found, null otherwise.
   */
  async checkForDuplicateName(designName) {
    if (!designName || !designName.trim()) {
      return null; // Empty names are handled elsewhere
    }

    const normalizedName = designName.trim().toLowerCase();
    const dataVault = this.experience?.dataVault;

    if (!dataVault) {
      log("DataVault not available, cannot check for duplicate names");
      return null; // Can't check, allow save (backend will validate)
    }

    try {
      // Fetch fresh designs from API to ensure we have the latest list
      log("Fetching fresh designs list to check for duplicate names...");
      await dataVault.fetchDesigns();

      // Get the updated designs list
      const designs = dataVault.designs || [];
      log(
        `Checking ${designs.length} designs for duplicate name: "${designName}"`,
      );

      for (const design of designs) {
        const existingName = (design.name || "").trim().toLowerCase();

        // Check if name matches
        if (existingName === normalizedName) {
          const designUuid = design.uuid || design.id;
          log(`Design with name "${designName}" found (UUID: ${designUuid})`);
          return design; // Return the matching design object
        }
      }

      log(`No design found with name: "${designName}"`);
      return null; // No matching design found
    } catch (error) {
      log("Error checking for duplicate names:", error);
      // If fetch fails, check against cached designs as fallback
      const cachedDesigns = dataVault.designs || [];
      log(`Falling back to cached designs (${cachedDesigns.length} designs)`);

      for (const design of cachedDesigns) {
        const existingName = (design.name || "").trim().toLowerCase();

        if (existingName === normalizedName) {
          const designUuid = design.uuid || design.id;
          log(
            `Design found in cached designs: "${designName}" (UUID: ${designUuid})`,
          );
          return design;
        }
      }

      // If we can't check properly, allow save (backend should also validate)
      log(
        "Could not verify duplicate names, allowing save (backend will validate)",
      );
      return null;
    }
  }

  /**
   * Validates a design name against existing designs and current project UUID.
   * Returns status indicating if name is available, same design, or conflicts with different design.
   * @param {string} designName - The design name to validate.
   * @param {string|null} currentProjectUuid - The UUID of the current project (null for new projects).
   * @returns {Promise<Object>} Object with status: "ok" | "same-design" | "conflict", and existingDesign/existingUuid if found.
   */
  async validateDesignName(designName, currentProjectUuid) {
    if (!designName || !designName.trim()) {
      return { status: "ok", existingDesign: null, existingUuid: null };
    }

    const existingDesign = await this.checkForDuplicateName(designName);

    if (!existingDesign) {
      return { status: "ok", existingDesign: null, existingUuid: null };
    }

    const existingUuid = existingDesign.uuid || existingDesign.id;

    // If UUIDs match, it's the same design
    if (currentProjectUuid && existingUuid === currentProjectUuid) {
      return { status: "same-design", existingDesign, existingUuid };
    }

    // Same name but different UUID - conflict
    return { status: "conflict", existingDesign, existingUuid };
  }

  /**
   * Formats design data from window.makeJson into API format.
   * @returns {Object} Object with figures and others arrays formatted for API.
   */
  formatDesignDataForSave() {
    const makeJson = window.makeJson || {};

    // Format figures according to API specification
    // For custom configured parts, preserve the full partData structure
    const figures = (makeJson.figures || []).map((figure) => {
      const mode = figure.partData?.mode;

      // For custom configured parts, preserve the entire partData structure
      if (mode === "user-cfg" || mode === "cfg-part") {
        return {
          uuid: figure.uuid,
          x: figure.x,
          y: figure.y,
          z: figure.z,
          angle: figure.angle,
          partData: {
            ...figure.partData, // Preserve all partData fields
            // Ensure required fields exist
            fullpartnumber:
              figure.partData?.fullpartnumber ||
              figure.partData?.partNumber ||
              figure.partData?.id ||
              "",
            layer: figure.partData?.layer || 30,
          },
        };
      } else {
        // For regular parts, only include required fields
        return {
          uuid: figure.uuid,
          x: figure.x,
          y: figure.y,
          z: figure.z,
          angle: figure.angle,
          partData: {
            fullpartnumber:
              figure.partData?.fullpartnumber ||
              figure.partData?.partNumber ||
              figure.partData?.id ||
              "",
            layer: figure.partData?.layer || 30,
          },
        };
      }
    });

    // Include textboxes (others) in the save payload
    const others = makeJson.others || [];

    return { figures, others };
  }

  /**
   * Shows a conflict dialog asking user to rename or overwrite.
   * @param {string} designName - The conflicting design name.
   * @param {Object} existingDesign - The existing design object.
   * @param {string} context - Context: "save" | "saveAs" | "create"
   */
  showConflictDialog(designName, existingDesign, context = "save") {
    const message = `A design with the name "${designName}" already exists. Would you like to rename your design or overwrite the existing one?`;

    this.eventHub.emit("modalInfo", {
      title: "Design Name Conflict",
      message: message,
      variant: "design-name-conflict",
      actions: [
        {
          label: "Rename",
          callback: () => {
            this.eventHub.emit("confirmRenameDesign", {
              designName,
              existingDesign,
              context,
            });
          },
        },
        {
          label: "Overwrite",
          callback: () => {
            this.eventHub.emit("confirmOverwriteDesign", {
              designName,
              existingDesign,
              context,
            });
          },
        },
        {
          label: "Cancel",
          callback: () => {
            // Just close the dialog, do nothing
          },
        },
      ],
    });
  }

  /**
   * Handles overwrite confirmation - sets current project UUID to existing design and saves.
   * @param {Object} eventData - Event data with designName, existingDesign, and context.
   */
  async handleConfirmOverwrite(eventData) {
    const { designName, existingDesign, context } = eventData;
    const existingUuid = existingDesign.uuid || existingDesign.id;

    log(
      "User confirmed overwrite for design:",
      designName,
      "UUID:",
      existingUuid,
    );

    // Set the current project UUID to the existing design's UUID
    // This makes subsequent saves treat it as updating the same design
    this.experience.projectUuid = existingUuid;
    window.currentProjectUuid = existingUuid;

    // Update toolbar name
    const projectNameElement = document.getElementById("toolbar-project-name");
    const projectNameContainer = document.getElementById(
      "toolbar-project-name-container",
    );
    if (projectNameElement) {
      projectNameElement.value = designName;
    }
    if (projectNameContainer) {
      projectNameContainer.style.display = "flex";
    }

    // Now proceed with save based on context
    if (context === "save") {
      // Trigger save with the existing UUID now set
      await this.saveDesign();
    } else if (context === "saveAs") {
      // For saveAs, close the modal and trigger save
      this.closeModal("backgroundModal", "save-modal");
      await this.saveDesign();
    } else if (context === "create") {
      // For create, close modals and start with a clean slate (empty design).
      // Next save will overwrite the existing design since projectUuid is already set.
      const backgroundModal = document.getElementById("backgroundModal");
      const newModal = document.getElementById("new-modal");
      const loadModal = document.getElementById("loadModal");
      if (backgroundModal) backgroundModal.style.display = "none";
      if (newModal) newModal.style.display = "none";
      if (loadModal) loadModal.style.display = "none";

      this.eventHub.emit("deleteScene");
      this.eventHub.emit("newProject", {
        event: "newProject",
        name: designName,
      });
    }
  }

  /**
   * Handles rename confirmation - shows the rename modal with a text input so the user can enter a new name.
   * @param {Object} eventData - Event data with designName, existingDesign, and context.
   */
  handleConfirmRename(eventData) {
    const { designName, context } = eventData;

    log("User confirmed rename, showing rename modal");

    if (context === "save") {
      // Show the rename modal (same as toolbar "Rename" flow) so user has a visible text prompt
      const renameModal = document.getElementById("rename-modal");
      const backgroundModal = document.getElementById("backgroundModal");
      const renameNameInput = document.getElementById("rename-name");
      const renameAlert = document.getElementById("rename-name-alert");

      if (renameModal && backgroundModal) {
        const currentName =
          designName ||
          (
            document.getElementById("toolbar-project-name")?.value || ""
          ).trim() ||
          "Untitled Design";
        if (renameNameInput) {
          renameNameInput.value = currentName;
          renameNameInput.placeholder = "Rename Your Design*";
        }
        if (renameAlert) {
          renameAlert.style.display = "none";
        }
        renameModal.style.display = "flex";
        backgroundModal.style.display = "flex";
        this.pendingSaveAfterRename = true;
        if (renameNameInput) {
          renameNameInput.focus();
          renameNameInput.select();
        }
      }
    } else if (context === "saveAs" || context === "create") {
      // Focus the save-as or create name input in their respective modals
      const nameInputId =
        context === "saveAs" ? "save-as-desing" : "design-name";
      const nameInput = document.getElementById(nameInputId);
      if (nameInput) {
        nameInput.focus();
        nameInput.select();
        const currentName = nameInput.value.trim();
        if (currentName) {
          nameInput.value = `${currentName} (copy)`;
        }
      }
    }
  }

  /**
   * Handles designRenamed event (fired when user submits new name in rename modal).
   * If the rename came from the save-conflict flow, re-trigger save with the new name.
   */
  handleDesignRenamed(eventData) {
    if (this.pendingSaveAfterRename) {
      log("Design renamed from conflict flow, re-triggering save");
      this.pendingSaveAfterRename = false;
      this.saveDesign();
    }
  }

  /**
   * Handles new project creation request with validation.
   * @param {Object} eventData - Event data with name property.
   */
  async handleRequestNewProject(eventData) {
    const { name } = eventData;

    if (!name || !name.trim()) {
      // Show alert for empty name
      const alerts = document.querySelectorAll("#design-name-alert");
      alerts.forEach((alert) => {
        alert.style.display = "flex";
      });
      return;
    }

    if (!this.user_authenticated) {
      this.failedToSave("Please Sign In to create a new design.");
      return;
    }

    // Validate design name (no current UUID for new projects)
    const validation = await this.validateDesignName(name.trim(), null);

    if (validation.status === "ok") {
      // Name is available, proceed with creation
      // Close modals
      const backgroundModal = document.getElementById("backgroundModal");
      const newModal = document.getElementById("new-modal");
      const loadModal = document.getElementById("loadModal");
      if (backgroundModal) backgroundModal.style.display = "none";
      if (newModal) newModal.style.display = "none";
      if (loadModal) loadModal.style.display = "none";

      // Clear previous design IDs - new design has no ID until saved
      this.experience.projectUuid = null;
      window.currentProjectUuid = null;
      if (this.experience.dataVault) {
        this.experience.dataVault.currentDesignId = null;
      }
      log("Cleared design IDs for new project");

      // Update UI
      const projectNameContainer = document.getElementById(
        "toolbar-project-name-container",
      );
      const projectNameElement = document.getElementById(
        "toolbar-project-name",
      );
      if (projectNameContainer) {
        projectNameContainer.style.display = "flex";
      }
      if (projectNameElement) {
        projectNameElement.value = name.trim();
      }

      // Emit newProject event to trigger scene creation
      this.eventHub.emit("deleteScene");
      this.eventHub.emit("newProject", {
        event: "newProject",
        name: name.trim(),
      });
    } else if (validation.status === "conflict") {
      // Name conflict - show dialog
      this.showConflictDialog(name.trim(), validation.existingDesign, "create");
    }
  }

  /**
   * Saves the current design project to the backend API.
   * Updates existing design if name matches and UUID matches, otherwise creates new.
   */
  async saveDesign() {
    if (!this.user_authenticated) {
      this.failedToSave("Please Sign In to save your design.");
      return;
    }
    if (this.experience.isLockedDesignView) {
      this.failedToSave(
        "This design is locked (RFQ sent). View only — saving is not allowed.",
      );
      return;
    }

    // Prevent concurrent saves
    if (this.isSaving) {
      log("Save already in progress, ignoring duplicate save request");
      return;
    }

    this.isSaving = true;

    try {
      const designNameElement = document.getElementById("toolbar-project-name");
      const designName = designNameElement?.value?.trim() || "Untitled Design";

      // Get the current project UUID
      const currentProjectUuid =
        this.experience.projectUuid || window.currentProjectUuid;

      // Validate design name
      const validation = await this.validateDesignName(
        designName,
        currentProjectUuid,
      );

      // Handle conflict - show dialog and return (user will choose rename/overwrite)
      if (validation.status === "conflict") {
        this.isSaving = false;
        this.showConflictDialog(designName, validation.existingDesign, "save");
        return;
      }

      // Format design data for API
      const { figures, others } = this.formatDesignDataForSave();

      // Use longer timeout for save operations (60 seconds) since designs can be large
      const saveTimeout = 60000;

      let response;
      let statusMessage;

      // Check if we should update existing design or create new one
      if (validation.status === "same-design") {
        // Same design - update existing
        const existingUuid = validation.existingUuid;
        log("Updating existing design with matching UUID:", existingUuid);

        // Build the full design object for PUT request (matching API format)
        const requestBody = {
          _id: validation.existingDesign._id, // MongoDB ID if present
          id: existingUuid, // UUID
          data: {
            figures: figures,
            others: others,
          },
          name: designName,
          customerId: validation.existingDesign.customerId || null,
          note: validation.existingDesign.note || "",
          description: validation.existingDesign.description || "",
          // Don't include createdAt, updatedAt, __v - these are managed by backend
        };

        log("Updating design to API:", {
          name: designName,
          uuid: existingUuid,
          figuresCount: figures.length,
          othersCount: others.length,
        });

        // PUT request to update existing design
        response = await this.dataProvider.update(
          "design",
          existingUuid,
          requestBody,
          saveTimeout,
        );
        log("Design updated successfully:", response);
        statusMessage = "Design updated successfully";
      } else {
        // No existing design with this name - create new
        log("Creating new design");

        const requestBody = {
          data: {
            figures: figures,
            others: others,
          },
          name: designName,
        };

        log("Creating new design to API:", {
          name: designName,
          figuresCount: figures.length,
          othersCount: others.length,
        });

        response = await this.dataProvider.create(
          "design",
          requestBody,
          saveTimeout,
        );

        // Store the returned UUID if provided
        if (response?.id || response?.uuid) {
          const newUuid = response.id || response.uuid;
          this.experience.projectUuid = newUuid;
          window.currentProjectUuid = newUuid;
          log("Stored new design UUID:", newUuid);
        }
        log("Design saved successfully:", response);
        statusMessage = "Design created successfully";
      }

      this.savedSuccessfully(statusMessage);
      const savedUuid =
        this.experience.projectUuid || window.currentProjectUuid;
      if (savedUuid) {
        this.eventHub.emit("designSaved", { uuid: savedUuid });
      }
    } catch (error) {
      log("Error saving design:", error);
      console.error("Failed to save design:", error);
      this.failedToSave(
        error.message || "Failed to save design. Please try again.",
      );
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Saves the design with a new name (creates a new design).
   * Updates existing design if name matches and UUID matches, otherwise creates new.
   * @param {HTMLElement} el - The element that triggered the action.
   * @param {string} id - The ID associated with the save action.
   */
  async saveAsDesign(el, id) {
    if (this.experience.isLockedDesignView) {
      this.failedToSave(
        "This design is locked (RFQ sent). View only — Save As is not allowed.",
      );
      return;
    }
    const designNameElement = document.getElementById("save-as-desing");
    const designName = designNameElement?.value?.trim() || "";

    if (designName === "") {
      document.getElementById("design-name-alert").style.display = "flex";
      return;
    }

    if (!this.user_authenticated) {
      this.failedToSave("Please Sign In to save your design.");
      return;
    }

    // Get the current project UUID
    const currentProjectUuid =
      this.experience.projectUuid || window.currentProjectUuid;

    // Validate design name
    const validation = await this.validateDesignName(
      designName,
      currentProjectUuid,
    );

    // Handle conflict - show dialog and return (user will choose rename/overwrite)
    if (validation.status === "conflict") {
      this.showConflictDialog(designName, validation.existingDesign, "saveAs");
      return;
    }

    try {
      // Format design data for API
      const { figures, others } = this.formatDesignDataForSave();

      // Use longer timeout for save operations (60 seconds) since designs can be large
      const saveTimeout = 60000;

      let response;
      let statusMessage;

      // Check if we should update existing design or create new one
      if (validation.status === "same-design") {
        // Same design - update existing
        const existingUuid = validation.existingUuid;
        log("Updating existing design with matching UUID:", existingUuid);

        // Build the full design object for PUT request (matching API format)
        const requestBody = {
          _id: validation.existingDesign._id, // MongoDB ID if present
          id: existingUuid, // UUID
          data: {
            figures: figures,
            others: others,
          },
          name: designName,
          customerId: validation.existingDesign.customerId || null,
          note: validation.existingDesign.note || "",
          description: validation.existingDesign.description || "",
          // Don't include createdAt, updatedAt, __v - these are managed by backend
        };

        log("Updating design to API:", {
          name: designName,
          uuid: existingUuid,
          figuresCount: figures.length,
          othersCount: others.length,
        });

        // PUT request to update existing design
        response = await this.dataProvider.update(
          "design",
          existingUuid,
          requestBody,
          saveTimeout,
        );
        log("Design updated successfully:", response);
        statusMessage = "Design updated successfully";
      } else {
        // No existing design with this name - create new
        log("Creating new design for Save As");

        const requestBody = {
          data: {
            figures: figures,
            others: others,
          },
          name: designName,
        };

        log("Creating new design to API:", {
          name: designName,
          figuresCount: figures.length,
          othersCount: others.length,
        });

        response = await this.dataProvider.create(
          "design",
          requestBody,
          saveTimeout,
        );

        // Store the new UUID
        if (response?.id || response?.uuid) {
          const newUuid = response.id || response.uuid;
          this.experience.projectUuid = newUuid;
          window.currentProjectUuid = newUuid;
          log("Stored new design UUID:", newUuid);
        }
        log("Design saved successfully as new:", response);
        statusMessage = "Design created successfully";
      }

      // Update the current design name in the toolbar
      const projectNameElement = document.getElementById(
        "toolbar-project-name",
      );
      if (projectNameElement) {
        projectNameElement.value = designName;
        log("Updated current design name to:", designName);
      }

      this.closeModal("backgroundModal", "save-modal");
      this.savedSuccessfully(statusMessage);
    } catch (error) {
      log("Error saving design as new:", error);
      console.error("Failed to save design as new:", error);
      this.failedToSave(
        error.message || "Failed to save design. Please try again.",
      );
    }
  }

  /**
   * Loads a design from an API response object (e.g. locked design from GET /api/v1/locked/design/:designId).
   * Used when opening the View Design link from RFQ email; design is read-only (isLockedDesignView set by Experience).
   * @param {Object} designResponse - API response with shape { data: { figures, others }, name, id }.
   */
  loadDesignFromData(designResponse) {
    if (!designResponse || typeof designResponse !== "object") {
      this.failedToSave("No design data provided.");
      return;
    }
    if (designResponse.error) {
      this.failedToSave(designResponse.error || "Failed to load design.");
      return;
    }
    // Support both { data: { figures } } and { data: { data: { figures } } }
    const designData =
      designResponse?.data?.figures != null
        ? designResponse.data
        : (designResponse?.data?.data ??
          designResponse?.data ??
          designResponse);
    const designName = designResponse?.name || "Untitled Design";
    const responseUuid =
      designResponse?.uuid ?? designResponse?.id ?? designResponse?.designId;

    const hasFigures =
      Array.isArray(designData?.figures) && designData.figures.length > 0;
    if (!hasFigures) {
      this.failedToSave(
        designResponse?.error ||
          "Design has no parts to display, or the design was not found.",
      );
      return;
    }

    const savedDesignsDropdown = document.getElementById(
      "options-saved-design",
    );
    if (savedDesignsDropdown) savedDesignsDropdown.style.display = "none";
    const backgroundModal = document.getElementById("backgroundModal");
    const loadModal = document.getElementById("loadModal");
    if (backgroundModal) backgroundModal.style.display = "none";
    if (loadModal) loadModal.style.display = "none";

    const projectNameElement = document.getElementById("toolbar-project-name");
    const projectNameContainer = document.getElementById(
      "toolbar-project-name-container",
    );
    if (projectNameElement) projectNameElement.value = designName;
    if (projectNameContainer) projectNameContainer.style.display = "flex";

    this.experience.projectUuid = responseUuid;
    window.currentProjectUuid = responseUuid;

    // Locked design: hide Save / Save As so the design cannot be changed
    const saveOptionsBtn = document.getElementById("saveOptions");
    if (saveOptionsBtn) saveOptionsBtn.style.display = "none";

    const normalizedData = this.normalizeDesignData(designData);
    this.eventHub.emit("deleteScene");
    // Direct makePartsLoader path so parts actually render (same as file load)
    const runLoad = () => {
      this.experience.undoRedoManager.clearHistory(
        "baseline: view-design load",
      );
      const payload = { eventType: "load", data: normalizedData };
      const enriched =
        this.experience.undoRedoManager.enrichPayloadWithObjectIds(payload);
      this.eventHub.emit("makePartsLoader", enriched.data);
      setTimeout(() => this.eventHub.emit("zoomAll"), 2500);
    };
    setTimeout(runLoad, 600);

    log("Design loaded from data (e.g. locked design)");
  }

  /**
   * Loads a design from the API when user clicks on a saved design.
   * @param {Object} eventData - Event data containing the design UUID.
   */
  async loadDesignFromApi(eventData) {
    log("loadDesignFromApi called with eventData:", eventData);

    // The event data structure is: { event: "viewProject", uuid: "..." }
    // Handle both uuid and id properties, and also handle if eventData is the UUID string directly
    let designUuid = null;

    if (typeof eventData === "string") {
      // If eventData is just a string (UUID), use it directly
      designUuid = eventData;
    } else if (eventData && typeof eventData === "object") {
      // If eventData is an object, try uuid first, then id
      designUuid = eventData.uuid || eventData.id || eventData.designId;
    }

    if (!designUuid) {
      log("No UUID provided in viewProject event. Event data:", eventData);
      log("Event data type:", typeof eventData);
      log(
        "Event data keys:",
        eventData && typeof eventData === "object"
          ? Object.keys(eventData)
          : "N/A",
      );
      this.failedToSave("No design ID provided");
      return;
    }

    if (!this.user_authenticated) {
      log("User not logged in — refusing to load design from API");
      this.failedToSave("Please sign in to load a design.");
      return;
    }

    try {
      log("Loading design from API with UUID:", designUuid);
      this.experience.isLockedDesignView = false;

      // Fetch design from API: GET /api/v1/design/{uuid}
      const designResponse = await this.dataProvider.getOne(
        "design",
        designUuid,
      );

      log("Design fetched from API:", designResponse);

      // Transform API response to loadProject format
      // API response likely has: { name, data: { figures: [...] }, uuid }
      const designData = designResponse?.data || designResponse;
      const designName = designResponse?.name || "Untitled Design";
      const responseUuid =
        designResponse?.uuid || designResponse?.id || designUuid;

      // Close the saved designs dropdown
      const savedDesignsDropdown = document.getElementById(
        "options-saved-design",
      );
      if (savedDesignsDropdown) {
        savedDesignsDropdown.style.display = "none";
      }

      // Hide initial view modals if they're showing
      const backgroundModal = document.getElementById("backgroundModal");
      const loadModal = document.getElementById("loadModal");
      if (backgroundModal) {
        backgroundModal.style.display = "none";
      }
      if (loadModal) {
        loadModal.style.display = "none";
      }

      // Update project name in UI
      const projectNameElement = document.getElementById(
        "toolbar-project-name",
      );
      const projectNameContainer = document.getElementById(
        "toolbar-project-name-container",
      );
      if (projectNameElement) {
        projectNameElement.value = designName;
      }
      if (projectNameContainer) {
        projectNameContainer.style.display = "flex";
      }

      // Store UUID for future saves
      this.experience.projectUuid = responseUuid;
      window.currentProjectUuid = responseUuid;

      // Load the design data
      if (designData) {
        log("Loading design data into scene");

        // Normalize the design data to ensure it has the expected structure
        // When loading from API, partData only has fullpartnumber and layer,
        // but the code expects properties and mode to exist
        const normalizedData = this.normalizeDesignData(designData);

        this.eventHub.emit("deleteScene");
        this.eventHub.emit("MakePartsEvent", {
          eventType: "load",
          data: normalizedData,
        });

        setTimeout(() => {
          this.eventHub.emit("zoomAll");
        }, 2500);
      }

      log("Design loaded successfully");
    } catch (error) {
      log("Error loading design from API:", error);
      console.error("Failed to load design:", error);
      this.failedToSave(
        error.message || "Failed to load design. Please try again.",
      );
      throw error; // rethrow so callers (e.g. tryViewDesignFromUrl) know the load failed
    }
  }

  /**
   * Normalizes design data loaded from the API to match the expected structure.
   * Ensures partData has properties and mode fields that the loading code expects.
   * @param {Object} designData - The design data from the API.
   * @returns {Object} - Normalized design data.
   */
  normalizeDesignData(designData) {
    const normalized = {
      figures: (designData.figures || []).map((figure) => {
        // Ensure partData has the expected structure
        const partData = figure.partData || {};

        // Ensure properties exists (even if empty) to prevent undefined errors
        if (!partData.properties) {
          partData.properties = {};
        }

        // Set mode if not present - default to "xm-part" for parts loaded from API
        if (!partData.mode) {
          partData.mode = "xm-part";
        }

        // Ensure fullpartnumber is accessible via multiple property names
        if (partData.fullpartnumber && !partData.partNumber) {
          partData.partNumber = partData.fullpartnumber;
        }

        return {
          ...figure,
          partData: {
            ...partData,
            // Preserve the original structure but ensure required fields exist
            fullpartnumber:
              partData.fullpartnumber || partData.partNumber || "",
            layer: partData.layer || 30,
            properties: partData.properties,
            mode: partData.mode,
          },
        };
      }),
      others: designData.others || [],
      version: designData.version || "2.0",
    };

    log("Normalized design data:", normalized);
    return normalized;
  }

  /**
   * Handles the "Save As" button action.
   */
  saveAsBtn() {
    if (this.experience.isLockedDesignView) {
      this.failedToSave(
        "This design is locked (RFQ sent). View only — Save As is not allowed.",
      );
      return;
    }
    const id = "save-modal";
    if (id == "save-modal" || id == "new-modal" || id == "rename-modal") {
      if (id == "save-modal" && !this.user_authenticated) {
        this.failedToSave("Please Sign In to save your design.");
        return;
      }
      document.getElementById(id).style.display = "flex";
      document.getElementById("backgroundModal").style.display = "flex";
    }
  }

  /**
   * Sends the current BOM data to the cart. No save prompt here; the "share design for quoting" prompt is shown at Send RFQ.
   */
  handleSendToCart() {
    const bomParts = this.bomData.getBomParts();
    const data = {
      event: "addToCart",
      bomParts,
      contents: JSON.stringify(window.makeJson),
    };
    log("EVENTHUB EVENT: ", data);
    this.eventHub.emit("addToCart", data);
  }

  /**
   * Displays a success message in the snackbar.
   * @param {string} text - The success message to display.
   */
  savedSuccessfully(text) {
    const snackbar = document.getElementById("snackbar");
    snackbar.innerHTML = '<i class="fa-solid fa-check"></i> ' + text;
    snackbar.className = "show";

    setTimeout(function () {
      snackbar.className = snackbar.className.replace("show", "");
    }, 3000);
  }

  /**
   * Displays an error message in the snackbar.
   * @param {string} text - The error message to display.
   */
  failedToSave(text) {
    const snackbar = document.getElementById("snackbar");
    snackbar.innerHTML = '<i class="fa-solid fa-xmark"></i> ' + text;
    snackbar.className = "show";

    setTimeout(function () {
      snackbar.className = snackbar.className.replace("show", "");
    }, 3000);
  }

  /**
   * Closes the specified modal elements.
   * @param {string} id - The ID of the first modal to close.
   * @param {string} id2 - The ID of the second modal to close.
   */
  closeModal(id, id2) {
    const modal = document.getElementById(id);
    const modal2 = document.getElementById(id2);
    if (modal) {
      modal.style.display = "none";
      modal2.style.display = "none";
    }
  }
}
